import React from 'react';
import AnimatedBlock from '~/components/atom/AnimatedBlock';
import JustificationContainer from '~/components/atom/JustificationContainer';
import type { BrandSettingsState } from '../../state';
import InputLabel from '~/components/atom/InputLabel';
import Input from '~/components/molecule/Input';
import type { DeepPartial } from 'utility-types';
import ColorInput from '~/components/molecule/ColorInput';
import TEST_ID from './index.testid';
import ImageInput from '~/components/organism/ImageInput';
import webSiteURLValidation from '~/util/websiteURLValidation';
import { Body } from '~/components/atom/Typography';
import Card from '~/components/atom/Card';
import Div from '~/components/atom/Div';
import styled, { css } from 'styled-components';
import GridContainer from '~/components/atom/GridContainer';

export type ImageUrls = {
  logo: string | undefined | null;
  teamImage: string | undefined | null;
  backgroundImage: string | undefined | null;
};

const text = {
  title: 'Huisstijl',
  primaryColor: 'Primaire tekstkleur',
  primaryBackgroundColor: 'Primaire kleur',
  secondaryColor: 'Secondaire tekstkleur',
  secondaryBackgroundColor: 'Secondaire kleur',
  url: 'Adres van je website',
  description:
    'Stel je huisstijl op deze pagina in. Vervolgens kan je in apps de huisstijl met één muisklik toepassen.',
  lorem: 'Lorem ipsum dolor sit amet.',
  preview: 'Voorbeeld',
};

export type Props = {
  values: Omit<BrandSettingsState, 'unsubscribeLinkText'>;

  /** Disables the input fields */
  readOnly: boolean;

  /** Current image urls to be displayed as previews */
  imageUrls: ImageUrls;

  /** Partial update of current changes state */
  onUpdate: (
    args: Omit<DeepPartial<BrandSettingsState>, keyof ImageUrls>,
  ) => void;

  /** Update handler for image fields */
  onImageUpdate: (update: {
    fieldName: keyof ImageUrls;
    value: { s3key: string; url: string } | undefined;
  }) => void;
};

const AccountBlock: React.FCC<Props> = ({
  values,
  onUpdate,
  readOnly,
  imageUrls,
  onImageUpdate,
  ...rest
}) => {
  const colors = values.colors;

  return (
    <AnimatedBlock
      title={text.title}
      manualLayout
      dataTestId={TEST_ID.CONTAINER}
      {...rest}
    >
      <JustificationContainer padding={['m', null]} gap="l" direction="column">
        <Body margin={[null]}>{text.description}</Body>

        <JustificationContainer direction="column" width="100%">
          <Input
            required
            label={{ text: text.url }}
            value={values.url}
            onChange={event => onUpdate({ url: event?.target.value || '' })}
            width="100%"
            disabled={readOnly}
            name="url"
            validation={[webSiteURLValidation]}
          />
        </JustificationContainer>

        <GridContainer gap="m" columns="1fr 1fr">
          <ImageContainer dataTestId={TEST_ID.LOGO_CONTAINER}>
            <InputLabel label="Logo" />
            <ImageInput
              initialUrl={imageUrls.logo}
              s3Key={values.logo?.s3key}
              filename="logo"
              onChange={value => {
                onImageUpdate({
                  fieldName: 'logo',
                  value,
                });
              }}
              readOnly={readOnly}
              dataTestId={TEST_ID.LOGO_IMAGE_INPUT}
            />
          </ImageContainer>
          <ImageContainer>
            <InputLabel label="Kantoorfoto" />
            <ImageInput
              initialUrl={imageUrls.teamImage}
              s3Key={values.teamImage?.s3key}
              filename="teamImage"
              onChange={value => {
                onImageUpdate({
                  fieldName: 'teamImage',
                  value,
                });
              }}
              readOnly={readOnly}
              dataTestId={TEST_ID.OFFICE_IMAGE_INPUT}
            />
          </ImageContainer>
          <ImageContainer>
            <InputLabel label="Achtergrondfoto" />
            <ImageInput
              initialUrl={imageUrls.backgroundImage}
              s3Key={values.backgroundImage?.s3key}
              filename="backgroundImage"
              onChange={value => {
                onImageUpdate({
                  fieldName: 'backgroundImage',
                  value,
                });
              }}
              readOnly={readOnly}
              dataTestId={TEST_ID.BACKGROUND_IMAGE_INPUT}
            />
          </ImageContainer>
        </GridContainer>

        <JustificationContainer width="100%" gap="l">
          <JustificationContainer gap="l" wrap="wrap" width="60%">
            <ColorPairContainer gap={'l'}>
              <ColorInput
                value={colors?.primary?.background || ''}
                onChange={color =>
                  onUpdate({ colors: { primary: { background: color } } })
                }
                label={text.primaryBackgroundColor}
                readOnly={readOnly}
                dataTestId="picker_design-primaryBackgroundColor"
              />
              <ColorInput
                value={colors?.primary?.color || ''}
                onChange={color => onUpdate({ colors: { primary: { color } } })}
                label={text.primaryColor}
                readOnly={readOnly}
                dataTestId="picker_design-primaryColor"
              />
            </ColorPairContainer>
            <ColorPairContainer gap={'l'}>
              <ColorInput
                value={colors?.secondary?.background || ''}
                onChange={color =>
                  onUpdate({ colors: { secondary: { background: color } } })
                }
                label={text.secondaryBackgroundColor}
                readOnly={readOnly}
                dataTestId="picker_design-secondaryBackgroundColor"
              />
              <ColorInput
                value={colors?.secondary?.color || ''}
                onChange={color =>
                  onUpdate({ colors: { secondary: { color } } })
                }
                label={text.secondaryColor}
                readOnly={readOnly}
                dataTestId="picker_design-secondaryColor"
              />
            </ColorPairContainer>
          </JustificationContainer>

          <PreviewContainer direction="column" width="40%">
            <InputLabel label={text.preview} />
            <StyledCard>
              <JustificationContainer direction="column">
                <TextContainer
                  padding={['s', 'base']}
                  $color={colors?.primary.color}
                  $backgroundColor={colors?.primary.background}
                >
                  {text.lorem}
                </TextContainer>

                <TextContainer
                  padding={['s', 'base']}
                  $color={colors?.secondary.color}
                  $backgroundColor={colors?.secondary.background}
                >
                  {text.lorem}
                </TextContainer>
              </JustificationContainer>
            </StyledCard>
          </PreviewContainer>
        </JustificationContainer>
      </JustificationContainer>
    </AnimatedBlock>
  );
};

const ImageContainer = styled(JustificationContainer)`
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const TextContainer = styled(Div)<{
  $color?: string;
  $backgroundColor?: string;
}>(
  ({ $color, $backgroundColor }) => css`
    width: 100%;
    color: ${$color};
    background-color: ${$backgroundColor};
  `,
);

const StyledCard = styled(Card)`
  width: 100%;
  margin: 0;
  overflow: hidden;
`;

const ColorPairContainer = styled(JustificationContainer)`
  width: 100%;
`;

const PreviewContainer = styled(JustificationContainer)``;

export default AccountBlock;
